/**
 * Page d'adhésion FTPE - Cotisation obligatoire
 * Pour Cooper, Member et Expert
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, CheckCircle, AlertTriangle, CreditCard, RefreshCw, X, Loader2, Star, Users, FileText, Award, ArrowRight, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

import axios from 'axios';
import { toast } from 'react-hot-toast';
import BackButton from '../components/BackButton';
import { getToken } from '../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Features du plan FTPE
const PLAN_FEATURES = [
  { icon: Shield, text: 'Accès complet à la plateforme COPPAM' },
  { icon: Users, text: 'Représentation fédérale TPE' },
  { icon: FileText, text: 'Dispositifs solidaires (RCDS, CCMIS, DOS)' },
  { icon: Award, text: 'Support et accompagnement personnalisé' },
  { icon: RefreshCw, text: 'Renouvellement automatique par CB' }
];

export default function AdhesionFTPE() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [adhesionStatus, setAdhesionStatus] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [error, setError] = useState('');

  const token = getToken();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    loadData();
  }, []);

  // Polling pour vérifier le statut du paiement après retour de Stripe
  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les infos du plan
      const planRes = await axios.get(`${API}/ftpe-adhesion/plan-info`);
      setPlanInfo(planRes.data);

      // Charger le statut de l'utilisateur
      const statusRes = await axios.get(`${API}/ftpe-adhesion/my-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdhesionStatus(statusRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setError('Vérification du paiement expirée. Veuillez rafraîchir la page.');
      return;
    }

    try {
      const response = await axios.get(`${API}/ftpe-adhesion/checkout-status/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentSuccess(true);
        setAdhesionStatus({
          ...adhesionStatus,
          valid: true,
          adhesion: data.adhesion
        });
        toast.success('🎉 Adhésion FTPE activée avec succès !');
        // Nettoyer l'URL
        window.history.replaceState({}, '', '/adhesion-ftpe');
        return;
      } else if (data.status === 'expired') {
        setError('La session de paiement a expiré. Veuillez réessayer.');
        return;
      }

      // Continuer le polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      }
    }
  };

  const handleSubscribe = async () => {
    setProcessing(true);
    setError('');

    try {
      const response = await axios.post(
        `${API}/ftpe-adhesion/subscribe`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.already_subscribed) {
        toast.success('Vous avez déjà une adhésion active !');
        setAdhesionStatus({ ...adhesionStatus, valid: true, adhesion: response.data.adhesion });
        return;
      }

      if (response.data.url) {
        // Rediriger vers Stripe Checkout
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
      toast.error('Erreur lors de la souscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelAutoRenew = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler le renouvellement automatique ?')) {
      return;
    }

    try {
      await axios.post(
        `${API}/ftpe-adhesion/cancel-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Renouvellement automatique désactivé');
      loadData();
    } catch {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Si l'utilisateur n'a pas besoin d'adhésion
  if (adhesionStatus && !adhesionStatus.required) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Adhésion non requise
              </h2>
              <p className="text-gray-600">
                L&apos;adhésion FTPE n&apos;est pas requise pour votre type de compte.
              </p>
              <Button 
                className="mt-6" 
                onClick={() => navigate(-1)}
              >
                Retour
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Affichage principal
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Bouton Retour */}
        <div className="mb-4">
          <BackButton label="Retour" homeLabel="Espace FTPE Outremer" />
        </div>
        
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="bg-purple-600 text-white px-4 py-1 mb-4 font-semibold">
            FÉDÉRATION TPE
          </Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Adhésion FTPE
          </h1>
          <p className="text-gray-600">
            Cotisation annuelle obligatoire pour accéder à la plateforme
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
            <Button variant="ghost" size="icon" onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Payment Success */}
        {paymentSuccess && (
          <div className="mb-6 p-6 bg-green-50 border-2 border-green-200 rounded-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-green-800 text-lg">Paiement confirmé !</h3>
                <p className="text-green-700">Votre adhésion FTPE est maintenant active.</p>
              </div>
            </div>
          </div>
        )}

        {/* Si adhésion active */}
        {adhesionStatus?.valid && adhesionStatus?.adhesion && (
          <Card className="mb-6 border-0 shadow-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                  <Shield className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-xl">Adhésion Active</h3>
                  <p className="text-white/80">
                    Valide jusqu&apos;au {formatDate(adhesionStatus.adhesion.expiration_date)}
                  </p>
                </div>
                <Badge className="bg-white text-slate-800/20 text-white border-0 font-semibold">
                  {adhesionStatus.adhesion.auto_renew ? 'Renouvellement auto' : 'Sans renouvellement'}
                </Badge>
              </div>

              {adhesionStatus.adhesion.auto_renew && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelAutoRenew}
                  className="mt-4 border-white/30 text-white hover:bg-white/10"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Annuler le renouvellement auto
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Plan Card */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-br from-purple-600 to-indigo-700 p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Star className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{planInfo?.plan?.name || 'Adhésion FTPE'}</h2>
                  <p className="text-white/70 text-sm">Cotisation annuelle</p>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-4xl font-bold">
                  {planInfo?.plan?.price || 120}€
                </span>
                <span className="text-white/70 ml-2">/ an</span>
              </div>
            </div>

            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Inclus dans votre adhésion</h3>
              <ul className="space-y-3">
                {(planInfo?.features || PLAN_FEATURES.map(f => f.text)).map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      {typeof feature === 'string' ? feature : feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Bouton de souscription */}
              {(!adhesionStatus?.valid || !adhesionStatus?.adhesion) && (
                <Button
                  onClick={handleSubscribe}
                  disabled={processing}
                  className="w-full mt-6 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Souscrire maintenant
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Informations importantes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-semibold text-amber-800 mb-2">Adhésion obligatoire</h4>
                <p className="text-sm text-amber-700">
                  L&apos;adhésion FTPE est obligatoire pour accéder aux dispositifs solidaires 
                  de la plateforme COPPAM (RCDS, CCMIS, DOS).
                </p>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Renouvellement automatique</h4>
                <p className="text-sm text-blue-700">
                  Votre adhésion sera automatiquement renouvelée à l&apos;échéance. 
                  Vous pouvez annuler le renouvellement à tout moment.
                </p>
              </div>

              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="font-semibold text-purple-800 mb-2">Paiement sécurisé</h4>
                <p className="text-sm text-purple-700">
                  Le paiement est traité de manière sécurisée par Stripe. 
                  Vos données bancaires ne sont jamais stockées sur nos serveurs.
                </p>
              </div>

              <div className="flex items-center justify-center gap-4 pt-4 border-t">
                <img 
                  src="https://cdn.brandfetch.io/idxAg10C0L/w/400/h/400/theme/dark/icon.jpeg?c=1id64Mup7ac02e8hBJC" 
                  alt="Stripe" 
                  className="h-8 opacity-60"
                />
                <span className="text-xs text-gray-400">Paiement sécurisé par Stripe</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            ← Retour
          </Button>
        </div>
      </div>
    </div>
  );
}
